import { PlusCircleIcon, XCircleIcon } from "@heroicons/react/24/outline"
import React from "react"

import HealMeTooltip from "../../components/shared/HealMeTooltip"
import { CheckBox, TimePicker } from "../../components/shared/Inputs"
import { ORDERED_DAYS } from "../../utils/utils"

const AvailableTimes = ({ availableTimes, updateState }) => {
  const updateAvailableTimes = (day, key, value) => {
    updateState((state) => {
      const newState = JSON.parse(JSON.stringify(state))
      const newTimes = { ...newState.availableTimes }
      newTimes[day] ||= {}
      newTimes[day][key] = value
      return { ...newState, availableTimes: newTimes }
    })
  }

  const addTimeBlock = (day) => {
    updateState((state) => {
      const newState = JSON.parse(JSON.stringify(state))
      const newTimes = { ...newState.availableTimes }

      // Initialize blocks if not exists
      if (!newTimes[day].blocks) {
        // If we have legacy format, convert it to blocks first
        if (newTimes[day].startsAt || newTimes[day].endsAt) {
          newTimes[day].blocks = [
            {
              startsAt: newTimes[day].startsAt || "9:00 AM",
              endsAt: newTimes[day].endsAt || "5:00 PM"
            }
          ]
        } else {
          newTimes[day].blocks = []
        }
      }

      // Add new block with default times
      newTimes[day].blocks.push({
        startsAt: "12:00 PM",
        endsAt: "5:00 PM"
      })

      return { ...newState, availableTimes: newTimes }
    })
  }

  const removeTimeBlock = (day, index) => {
    updateState((state) => {
      const newState = JSON.parse(JSON.stringify(state))
      const newTimes = { ...newState.availableTimes }

      // If it's the last block, disable the day instead of removing the block
      if (newTimes[day].blocks.length === 1) {
        newTimes[day].enabled = false
      } else {
        newTimes[day].blocks.splice(index, 1)
      }

      return { ...newState, availableTimes: newTimes }
    })
  }

  const updateBlockTime = (day, index, field, value) => {
    updateState((state) => {
      const newState = JSON.parse(JSON.stringify(state))
      const newTimes = { ...newState.availableTimes }

      if (!newTimes[day].blocks) {
        // Convert legacy format to blocks
        newTimes[day].blocks = [
          {
            startsAt: newTimes[day].startsAt || "9:00 AM",
            endsAt: newTimes[day].endsAt || "5:00 PM"
          }
        ]
      }

      newTimes[day].blocks[index][field] = value

      return { ...newState, availableTimes: newTimes }
    })
  }

  return (
    <div className="flex flex-col gap-2">
      {ORDERED_DAYS.map((day) => {
        const availableTimesForDay = availableTimes[day]

        const enabled = !availableTimesForDay
          ? false
          : typeof availableTimesForDay.enabled === "undefined"
          ? !!(availableTimesForDay.startsAt && availableTimesForDay.endsAt)
          : availableTimesForDay.enabled

        // Determine if we're using blocks or legacy format
        const hasBlocks = availableTimesForDay?.blocks && Array.isArray(availableTimesForDay.blocks)

        return (
          <div key={day} className="mb-2 flex flex-row">
            {/* Day header with checkbox */}
            <div className="flex items-start">
              <CheckBox
                id={`${day}-checkbox`}
                label={day}
                checked={enabled}
                onChange={() => {
                  updateAvailableTimes(day, "enabled", !enabled)
                  if (!enabled) {
                    // Initialize with default values if enabling
                    if (!hasBlocks) {
                      updateAvailableTimes(day, "blocks", [
                        {
                          startsAt: "9:00 AM",
                          endsAt: "5:00 PM"
                        }
                      ])
                    } else if (availableTimesForDay.blocks.length === 0) {
                      updateAvailableTimes(day, "blocks", [
                        {
                          startsAt: "9:00 AM",
                          endsAt: "5:00 PM"
                        }
                      ])
                    }

                    // For backward compatibility
                    if (!availableTimesForDay?.startsAt) updateAvailableTimes(day, "startsAt", "9:00 AM")
                    if (!availableTimesForDay?.endsAt) updateAvailableTimes(day, "endsAt", "5:00 PM")
                  }
                }}
                className="mb-0 w-32 py-1.5 pr-3"
              />

              {!enabled && <span className="my-[7px] flex-1 text-gray-dark">Unavailable</span>}
            </div>

            {/* Time blocks */}
            {enabled && (
              <div className="flex w-full flex-col gap-2">
                {hasBlocks ? (
                  // New format with blocks
                  availableTimesForDay.blocks.map((block, index) => (
                    <div key={index} className="flex items-center">
                      <div className="flex items-center">
                        <span>
                          <TimePicker
                            valueFormat="h:mm A"
                            value={block.startsAt || "9:00 AM"}
                            onChange={(e) => updateBlockTime(day, index, "startsAt", e.target.value)}
                          />
                        </span>
                        <span className="px-2"> - </span>
                        <span>
                          <TimePicker
                            valueFormat="h:mm A"
                            value={block.endsAt || "5:00 PM"}
                            onChange={(e) => updateBlockTime(day, index, "endsAt", e.target.value)}
                          />
                        </span>
                      </div>
                      {/* this should have fixed width if the + is there or not */}
                      <div className="ml-2 flex flex-1 items-center ">
                        <XCircleIcon
                          className="h-6 w-6 cursor-pointer text-gray-dark hover:text-red"
                          onClick={() => removeTimeBlock(day, index)}
                          data-testid="remove-time-block"
                          data-tooltip-id={`remove-block-${day}-${index}`}
                        />
                        {index === 0 ? (
                          <>
                            <PlusCircleIcon
                              className="ml-2 h-6 w-6 cursor-pointer text-gray-dark hover:text-teal"
                              onClick={() => addTimeBlock(day)}
                              data-tooltip-id={`add-block-${day}`}
                            />
                            <HealMeTooltip
                              id={`add-block-${day}`}
                              place="top"
                              render={() => <div>New interval for {day}</div>}
                            />
                          </>
                        ) : (
                          <div className="ml-2 h-6 w-6" />
                        )}
                        <HealMeTooltip
                          id={`remove-block-${day}-${index}`}
                          place="top"
                          render={() => <div>Remove time block</div>}
                        />
                      </div>
                    </div>
                  ))
                ) : (
                  // Legacy format
                  <div className="flex items-center">
                    <span>
                      <TimePicker
                        valueFormat="h:mm A"
                        value={availableTimesForDay?.startsAt || "9:00 AM"}
                        onChange={(e) => updateAvailableTimes(day, "startsAt", e.target.value)}
                      />
                    </span>
                    <span className="px-2"> - </span>
                    <span>
                      <TimePicker
                        valueFormat="h:mm A"
                        value={availableTimesForDay?.endsAt || "5:00 PM"}
                        onChange={(e) => updateAvailableTimes(day, "endsAt", e.target.value)}
                      />
                    </span>
                    <div className="ml-2 flex items-center">
                      <XCircleIcon
                        className="h-6 w-6 cursor-pointer text-gray-dark hover:text-red"
                        onClick={() => updateAvailableTimes(day, "enabled", false)}
                        data-testid="remove-time-block"
                        data-tooltip-id={`remove-block-${day}`}
                      />
                      <PlusCircleIcon
                        className="ml-2 h-6 w-6 cursor-pointer text-gray-dark hover:text-teal"
                        onClick={() => addTimeBlock(day)}
                        data-tooltip-id={`add-block-${day}`}
                      />
                      <HealMeTooltip
                        id={`add-block-${day}`}
                        place="top"
                        render={() => <div>New interval for {day}</div>}
                      />
                      <HealMeTooltip
                        id={`remove-block-${day}`}
                        place="top"
                        render={() => <div>Remove time block</div>}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default AvailableTimes
